import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar, TextBox, Button, Icon, Card } from '../../../components/common';
import { authService } from '../../../api/services/authService';

const CODE_EXPIRE_SEC = 300;   // 인증코드 만료: 5분
const RESEND_COOLDOWN_SEC = 60; // 재전송 쿨다운: 60초
const MAX_RESEND = 5;           // 최대 재전송 횟수
const MAX_VERIFY_ATTEMPTS = 5;  // 최대 인증 시도 횟수

export default function PasswordResetPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // step: 'email' → 'verify' → 'done'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // 인증코드 만료 타이머
  const [codeExpireLeft, setCodeExpireLeft] = useState(0);
  const expireTimerRef = useRef(null);

  // 재전송 쿨다운
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimerRef = useRef(null);

  // 횟수 제한
  const [resendCount, setResendCount] = useState(0);
  const [verifyAttempts, setVerifyAttempts] = useState(0);

  const isCodeExpired = step === 'verify' && codeExpireLeft <= 0;
  const isVerifyLocked = verifyAttempts >= MAX_VERIFY_ATTEMPTS;
  const isResendLocked = resendCount >= MAX_RESEND;

  // 타이머 정리
  useEffect(() => {
    return () => {
      if (expireTimerRef.current) clearInterval(expireTimerRef.current);
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, []);

  // 만료 타이머 시작 (서버 expires_in 우선 사용)
  const startExpireTimer = useCallback((seconds = CODE_EXPIRE_SEC) => {
    if (expireTimerRef.current) clearInterval(expireTimerRef.current);
    setCodeExpireLeft(seconds);
    expireTimerRef.current = setInterval(() => {
      setCodeExpireLeft((prev) => {
        if (prev <= 1) { clearInterval(expireTimerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // 쿨다운 타이머 시작
  const startCooldownTimer = useCallback(() => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    setResendCooldown(RESEND_COOLDOWN_SEC);
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownTimerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Step 1: 이메일로 인증코드 발송
  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    const newErrors = {};
    if (!email.trim()) newErrors.email = t('auth.reset_alert_email');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = t('auth.reset_alert_email_format');
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});
    setSubmitting(true);
    try {
      const res = await authService.passwordResetRequest({ email: email.trim() });
      setStep('verify');
      startExpireTimer(res.expires_in || CODE_EXPIRE_SEC);
      startCooldownTimer();
      setVerifyAttempts(0);
      setCode('');
    } catch (err) {
      // 429: 재전송 횟수 초과
      if (err.response?.status === 429) {
        setErrors({ email: err.response.data?.detail || t('auth.reset_alert_resend_limit') });
        return;
      }
      // 보안상 이메일 존재 여부 무관하게 다음 단계로 이동
      setStep('verify');
      startExpireTimer(CODE_EXPIRE_SEC);
      startCooldownTimer();
      setVerifyAttempts(0);
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  // 재전송
  const handleResend = async () => {
    if (resendCooldown > 0 || isResendLocked || submitting) return;
    setResendCount((prev) => prev + 1);
    setSubmitting(true);
    try {
      const res = await authService.passwordResetRequest({ email: email.trim() });
      startExpireTimer(res.expires_in || CODE_EXPIRE_SEC);
      startCooldownTimer();
      setVerifyAttempts(0);
      setCode('');
      setErrors({});
    } catch (err) {
      if (err.response?.status === 429) {
        setErrors({ code: err.response.data?.detail || t('auth.reset_alert_resend_limit') });
        setResendCount(MAX_RESEND); // 잠금 처리
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: 인증코드 + 새 비밀번호로 재설정
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (isCodeExpired) { setErrors({ code: t('auth.reset_alert_code_expired') }); return; }
    if (isVerifyLocked) { setErrors({ code: t('auth.reset_alert_verify_locked') }); return; }

    const newErrors = {};
    if (!code.trim()) newErrors.code = t('auth.reset_alert_code');
    if (!newPassword.trim()) newErrors.newPassword = t('auth.reset_alert_password');
    else if (newPassword.length < 6) newErrors.newPassword = t('auth.reset_alert_password_min');
    if (newPassword !== newPasswordConfirm) newErrors.newPasswordConfirm = t('auth.reset_alert_password_mismatch');
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});
    setSubmitting(true);
    try {
      await authService.passwordReset({ email: email.trim(), code: code.trim(), new_password: newPassword });
      if (expireTimerRef.current) clearInterval(expireTimerRef.current);
      setStep('done');
    } catch (err) {
      setVerifyAttempts((prev) => prev + 1);
      const detail = err.response?.data?.detail;
      const remaining = MAX_VERIFY_ATTEMPTS - verifyAttempts - 1;
      if (detail?.includes?.('code') || detail?.includes?.('Code') || detail?.includes?.('인증') || detail?.includes?.('만료')) {
        setErrors({ code: remaining > 0 ? `${t('auth.reset_alert_invalid_code')} (${t('auth.reset_attempts_remaining', { count: remaining })})` : t('auth.reset_alert_verify_locked') });
      } else {
        setErrors({ code: t('auth.reset_alert_failed') });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-[calc(100vh-59px)] bg-mono-50">
        <div className="w-full max-w-narrow mx-auto px-2.5 sm:px-5 pt-8 sm:pt-10 pb-12 sm:pb-16">
          {/* Logo */}
          <div className="flex justify-center"><img src="/images/logo/logo.png" alt="VIP VAN TOUR" className="h-10" /></div>

          {/* Card */}
          <Card variant="auth" className="mt-4">
            {/* Header with back arrow */}
            <div className="relative flex items-center justify-center mb-4">
              <button
                onClick={() => step === 'email' ? navigate(-1) : setStep('email')}
                className="absolute left-0 p-1 text-mono-900 hover:text-orange-500 transition"
              >
                <Icon name="chevron-left" size={24} />
              </button>
              <h2 className="text-xl font-semibold text-mono-900">{t('auth.reset_title')}</h2>
            </div>

            {/* Step 1: 이메일 입력 */}
            {step === 'email' && (
              <>
                <p className="text-sm text-mono-500 text-center mb-6">
                  {t('auth.reset_desc')}
                </p>
                <form onSubmit={handleSendCode} className="space-y-4">
                  <TextBox
                    label={t('auth.email')}
                    placeholder={t('auth.email_placeholder')}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors({}); }}
                    error={errors.email}
                  />
                  <Button type="submit" level="level1" fullWidth disabled={submitting}>
                    {submitting ? t('auth.reset_sending') : t('auth.reset_send_code')}
                  </Button>
                </form>
              </>
            )}

            {/* Step 2: 인증코드 + 새 비밀번호 */}
            {step === 'verify' && (
              <>
                <p className="text-sm text-mono-500 text-center mb-2 break-keep">
                  <span className="font-bold text-mono-900">{email}</span>{t('auth.reset_verify_desc')}
                </p>

                {/* 만료 타이머 */}
                <div className="text-center mb-4">
                  {isCodeExpired ? (
                    <span className="text-sm font-medium text-red-500">{t('auth.reset_code_expired')}</span>
                  ) : (
                    <span className={`text-sm font-bold ${codeExpireLeft <= 60 ? 'text-red-500' : 'text-orange-500'}`}>
                      {t('auth.reset_time_left')} {formatTime(codeExpireLeft)}
                    </span>
                  )}
                </div>

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <TextBox
                    label={t('auth.reset_code')}
                    placeholder={t('auth.reset_code_placeholder')}
                    value={code}
                    onChange={(e) => { setCode(e.target.value); setErrors((p) => ({ ...p, code: '' })); }}
                    error={errors.code}
                    disabled={isCodeExpired || isVerifyLocked}
                  />
                  <TextBox
                    label={t('auth.reset_new_password')}
                    placeholder={t('auth.reset_new_password_placeholder')}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setErrors((p) => ({ ...p, newPassword: '' })); }}
                    type="password"
                    error={errors.newPassword}
                    disabled={isCodeExpired || isVerifyLocked}
                  />
                  <TextBox
                    label={t('auth.reset_new_password_confirm')}
                    placeholder={t('auth.reset_new_password_confirm_placeholder')}
                    value={newPasswordConfirm}
                    onChange={(e) => { setNewPasswordConfirm(e.target.value); setErrors((p) => ({ ...p, newPasswordConfirm: '' })); }}
                    type="password"
                    error={errors.newPasswordConfirm}
                    disabled={isCodeExpired || isVerifyLocked}
                  />
                  <Button type="submit" level="level1" fullWidth disabled={submitting || isCodeExpired || isVerifyLocked}>
                    {submitting ? t('auth.reset_changing') : t('auth.reset_change')}
                  </Button>
                </form>

                {/* 재전송 버튼 */}
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || isResendLocked || submitting}
                  className={`block w-full text-center text-sm mt-3 transition ${
                    resendCooldown > 0 || isResendLocked ? 'text-mono-300 cursor-not-allowed' : 'text-mono-400 hover:text-mono-600'
                  }`}
                >
                  {isResendLocked
                    ? t('auth.reset_resend_locked')
                    : resendCooldown > 0
                      ? t('auth.reset_resend_cooldown', { sec: resendCooldown })
                      : `${t('auth.reset_resend')} (${resendCount}/${MAX_RESEND})`
                  }
                </button>
              </>
            )}

            {/* Step 3: 완료 */}
            {step === 'done' && (
              <div className="text-center space-y-4">
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto">
                  <Icon name="check" size={24} className="text-green-500" />
                </div>
                <p className="text-sm text-mono-900 font-medium">
                  {t('auth.reset_done')}
                </p>
                <Button level="level1" fullWidth onClick={() => navigate('/login')}>
                  {t('auth.reset_back_to_login')}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </main>
    </>
  );
}
